import { useEffect, useState, useRef, useCallback } from 'react';
import { get } from '../../lib/api-client';

interface QueueStats {
  name: string;
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
}

export function Queues() {
  const [queues, setQueues] = useState<QueueStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchQueues = useCallback(async () => {
    try {
      const data = await get<{ queues: QueueStats[] }>('/v1/admin/queues');
      setQueues(data.queues);
      setLastUpdated(new Date());
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load queue stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchQueues();
    intervalRef.current = setInterval(() => void fetchQueues(), 10000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchQueues]);

  if (loading) return <p className="text-sm text-ll-muted">Loading...</p>;
  if (error)
    return (
      <div className="bg-ll-error/10 border border-ll-error/30 rounded-lg p-4 text-sm text-ll-error">
        {error}
      </div>
    );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-ll-text">Queue Health</h2>
        {lastUpdated && (
          <span className="text-xs text-ll-muted">
            Auto-refreshing every 10s &middot; Last: {lastUpdated.toLocaleTimeString()}
          </span>
        )}
      </div>

      {queues.length === 0 ? (
        <p className="text-sm text-ll-muted">No queues available.</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {queues.map((q) => {
            const total = q.waiting + q.active + q.completed + q.failed + q.delayed;
            const healthColor =
              q.failed > 10
                ? 'border-ll-error/50'
                : q.waiting > 100
                  ? 'border-ll-amber/50'
                  : 'border-ll-border';

            return (
              <div key={q.name} className={`bg-ll-surface border ${healthColor} rounded-xl p-6`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-ll-text">{q.name}</h3>
                  <span
                    className={`text-xs px-2 py-0.5 rounded ${
                      q.failed > 10
                        ? 'bg-ll-error/20 text-ll-error'
                        : q.waiting > 100
                          ? 'bg-ll-amber/20 text-ll-amber'
                          : 'bg-ll-success/20 text-ll-success'
                    }`}
                  >
                    {q.failed > 10 ? 'Critical' : q.waiting > 100 ? 'Warning' : 'Healthy'}
                  </span>
                </div>

                <div className="grid grid-cols-5 gap-4">
                  {(
                    [
                      ['Waiting', q.waiting, q.waiting > 100],
                      ['Active', q.active, false],
                      ['Completed', q.completed, false],
                      ['Failed', q.failed, q.failed > 0],
                      ['Delayed', q.delayed, q.delayed > 50],
                    ] as [string, number, boolean][]
                  ).map(([label, value, warn]) => (
                    <div key={label}>
                      <p className="text-xs text-ll-muted mb-1">{label}</p>
                      <p className={`text-lg font-bold ${warn ? 'text-ll-error' : 'text-ll-text'}`}>
                        {value.toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>

                {total > 0 && (
                  <div className="mt-4 h-2 bg-ll-bg rounded-full overflow-hidden flex">
                    {q.completed > 0 && (
                      <div
                        className="bg-ll-success h-full"
                        style={{ width: `${(q.completed / total) * 100}%` }}
                      />
                    )}
                    {q.active > 0 && (
                      <div
                        className="bg-ll-cyan h-full"
                        style={{ width: `${(q.active / total) * 100}%` }}
                      />
                    )}
                    {q.waiting > 0 && (
                      <div
                        className="bg-ll-amber h-full"
                        style={{ width: `${(q.waiting / total) * 100}%` }}
                      />
                    )}
                    {q.failed > 0 && (
                      <div
                        className="bg-ll-error h-full"
                        style={{ width: `${(q.failed / total) * 100}%` }}
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
